/* import __COLOCATED_TEMPLATE__ from './time-series.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import { use } from 'ember-resources/util/function-resource';
import type {
  Options,
  Point,
  SeriesArearangeOptions,
  TooltipPositionerPointObject,
} from 'highcharts';
import {
  flattenSegmentBy,
  getNumericValue,
  type MetricValue,
  type Series,
} from 'embercom/lib/reporting/chart-data-resource';
import type { ReportState } from 'embercom/components/reporting/custom/report/chart-card';
import { cached } from 'tracked-toolbox';
import Highcharts from 'highcharts';
import { addTopicFilter, colorForNoMetricValue, metricConfigs } from './common';
import moment, { type Moment } from 'moment-timezone';
import type Range from 'embercom/models/reporting/range';
import { closestSafeIntervalFor } from 'embercom/lib/reporting/custom/safe-limits';
import { inject as service } from '@ember/service';
import type ThemeService from 'embercom/services/theme-service';
import PALETTE from '@intercom/pulse/lib/palette';

interface Args {
  reportState: ReportState;
  limit: number;
  metricId: string;
  topicId: string | null;
  onShowConversationExplorer: (
    topic: string,
    subtopic: string | null,
    dateRange: Range,
    conversationCount: number,
  ) => void;
}

interface TimeSeriesPoint extends Point {
  metricValue: MetricValue;
  volume: number;
  colorAxisValue: number;
}

function sumAllValues(series: Series) {
  return series
    .map(([_key, metricValue]) => getNumericValue(metricValue))
    .compact()
    .reduce((a, b) => a + b, 0);
}

function getAllValues(series: Series) {
  return series.map(([_timestamp, metricValue]) => getNumericValue(metricValue)).compact();
}

// given the palette and a value between 0 and 1, return the color at that point in the palette
// and interpolate between colors if the value is not exactly at a break point in the palette
function interpolateColor(palette: Highcharts.ColorType[], value: number) {
  let paletteValue = value * (palette.length - 1);
  let paletteIndex1 = Math.floor(paletteValue);
  let paletteIndex2 = Math.ceil(paletteValue);
  let interpolationDistance = paletteValue - paletteIndex1;

  let color1 = Highcharts.color(palette[paletteIndex1]);
  let color2 = Highcharts.color(palette[paletteIndex2]);

  return color1.tweenTo(color2, interpolationDistance);
}

function getColorForValue(
  palette: Highcharts.ColorType[],
  value: number,
  minValue: number,
  maxValue: number,
) {
  let range = maxValue - minValue;
  let normalizedValue = range === 0 ? 0.5 : (value - minValue) / range;
  return interpolateColor(palette, normalizedValue);
}

// the existing Range class quantized the range, e.g. endMoment uses .endOf('day')
// so it won't work with small intervals and has strange behaviour for ranges of a single day
// this class is a simpler version that looks the same, but just uses the raw numbers
// I've confirmed that the query on the backend uses gte start and lt end.
class SimpleDateTimeRange {
  startMoment: Moment;
  endMoment: Moment;
  timezone: string;

  constructor(start: number, end: number, timezone: string) {
    this.startMoment = moment.tz(start, timezone);
    this.endMoment = moment.tz(end, timezone);
    this.timezone = timezone;
  }
}

const maxOverlap = 1.2;

export default class TimeSeries extends Component<Args> {
  @service declare themeService: ThemeService;

  @use topicsData = staticDataResource(this, {
    serializedChart: () => this.chartDefinition,
    reportState: () => this.args.reportState,
  });

  get metricConfig() {
    return metricConfigs[this.args.metricId];
  }

  get timeBucketInterval() {
    if (this.timeInterval === 'hour') {
      return 1000 * 60 * 60;
    } else if (this.timeInterval === 'day') {
      return 1000 * 60 * 60 * 24;
    }
    return 0;
  }

  get timeInterval() {
    let days = this.args.reportState.dateRange.inDays;
    if (days === 1) {
      return 'hour';
    }
    return closestSafeIntervalFor('day', days);
  }

  get timestampOffsetForCurrentInterval() {
    if (this.timeInterval === 'hour') {
      return 1000 * 60 * 30;
    } else if (this.timeInterval === 'day') {
      return 1000 * 60 * 60 * 12;
    } else if (this.timeInterval === 'week') {
      return 1000 * 60 * 60 * 24 * 7;
    }
    return 0;
  }

  get chartDefinition() {
    if (this.args.topicId === null) {
      return this.topicsChartDefinition;
    }
    return this.subtopicChartDefinition;
  }

  get topicsChartDefinition() {
    return {
      view_by: 'time',
      view_by_time_interval: this.timeInterval,
      segment_by: 'conversation_custom_fields#ai_insights_topic',
      segment_by_display_limit: 500,
      chart_series: [{ metric_id: 'v1.new_conversations' }, this.metricConfig.seriesConfig],
    };
  }

  get subtopicChartDefinition() {
    if (this.args.topicId === null) {
      throw new Error('topicId is required for subtopic chart definition');
    }

    return {
      view_by: 'time',
      view_by_time_interval: this.timeInterval,
      segment_by: 'conversation_custom_fields#ai_insights_topic_subtopic',
      segment_by_display_limit: 500,
      chart_series: [
        addTopicFilter({ metric_id: 'v1.new_conversations' }, this.args.topicId),
        addTopicFilter(this.metricConfig.seriesConfig, this.args.topicId),
      ],
    };
  }

  get highchartsOptions(): Options & { tooltip?: { crosshairs?: boolean } } {
    // eslint-disable-next-line @intercom/intercom/no-bare-strings
    let dateTimeFormat = this.timeInterval === 'hour' ? 'D MMM HH:mm' : 'D MMM';
    if (!this.highchartsSeries) {
      return {};
    }
    let stops: Array<[number, string]> = this.metricConfig.colorAxis.stops.map(
      ([index, paletteColor]) => [index, PALETTE[paletteColor]],
    );

    // noop to force recalculation when the theme changes
    this.themeService.darkModeEnabled;

    let component = this;
    return {
      chart: {
        zooming: {
          type: 'x',
          mouseWheel: true,
        },
        panning: {
          type: 'x',
          enabled: true,
        },
        panKey: 'shift',
      },
      time: {
        timezone: component.args.reportState.dateRange.timezone,
      },
      legend: {
        enabled: true,
      },
      colorAxis: {
        min: this.extremes.minMetricValue,
        max: this.extremes.maxMetricValue,
        stops,
        labels: {
          useHTML: true,
          step: 2,
          formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
            return component.topicsData.formatValue(
              this.value as any,
              component.args.metricId,
              component.metricConfig.seriesConfig?.aggregation,
            );
          },
        },
      },
      tooltip: {
        useHTML: true,
        shared: false,
        crosshairs: true,
        // eslint-disable-next-line object-shorthand
        positioner: function (
          tooltipWidth: number,
          _tooltipHeight: number,
          point: TooltipPositionerPointObject & { h: number },
        ) {
          let maxX = this.chart.plotWidth - tooltipWidth / 2 + 20;
          return {
            x: Math.round(Math.min(point.plotX, maxX)),
            y: this.chart.hoverPoint!.plotY! + 20, // Position below the bar
          };
        },
        formatter(this: TimeSeriesPoint) {
          let formattedMetricValue = component.topicsData.formatValue(
            this.metricValue,
            component.args.metricId,
            component.metricConfig.seriesConfig?.aggregation,
          );
          // eslint-disable-next-line @intercom/intercom/no-bare-strings
          return `
          <b>Date:</b> ${moment
            .tz(this.x, component.args.reportState.dateRange.timezone)
            .format(dateTimeFormat)}<br />
          <b>Volume:</b> ${this.volume}<br />
          <b>${component.metricConfig.label}:</b> ${formattedMetricValue}
          `;
        },
      },
      plotOptions: {
        series: {
          animation: false,
          marker: {
            enabled: false,
          },
          states: {
            hover: {
              enabled: false,
            },
          },
          lineWidth: 1,
          cursor: 'pointer',
          point: {
            events: {
              click(this: TimeSeriesPoint, _event: Highcharts.PointClickEventObject) {
                let rangeStart = this.x;
                let rangeEnd = this.x + component.timeBucketInterval;
                let timeBucketRange = new SimpleDateTimeRange(
                  rangeStart as any,
                  rangeEnd as any,
                  component.args.reportState.dateRange.timezone,
                );
                let [topicId, subtopicId] = this.series.userOptions.id!.split('=>');
                component.args.onShowConversationExplorer(
                  topicId,
                  subtopicId ?? null,
                  timeBucketRange as any,
                  this.volume,
                );
              },
            },
          },
        },
      },
      xAxis: {
        type: 'datetime',
        plotBands: this.buildPlotBandsForWeekends(),
      },
      yAxis: {
        title: { text: null },
        categories: this.highchartsSeries.map((series) => series.name ?? ''),
        max: this.highchartsSeries.length,
        labels: {
          formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
            // this is a hack to give us enough room on the y-axis for all the series
            // but to avoid showing an extra label for the last series
            if (this.pos < this.chart.series.length) {
              return this.value as string;
            }
            return '';
          },
        },
        type: 'category',
        minPadding: 0,
        maxPadding: 0,
        min: 0,
        startOnTick: true,
        endOnTick: true,
        gridLineWidth: 1,
        tickmarkPlacement: 'on',
      },
    };
  }

  @cached
  get segmentedVolumeData() {
    let allSeries = flattenSegmentBy(this.topicsData.rawChartData[0]);
    // TODO: the backend is possibly sorting and limiting for us, but we should confirm
    allSeries.sort((a, b) => {
      let [_seriesKey, series] = a;
      let [_otherSeriesKey, otherSeries] = b;
      return sumAllValues(otherSeries) - sumAllValues(series);
    });

    let limited = allSeries.slice(0, this.args.limit);
    return new Map(limited);
  }

  @cached
  get segmentedMetricData() {
    let allSeries = flattenSegmentBy(this.topicsData.rawChartData[1]);
    let topSeries = allSeries.filter(([seriesKey, _series]) => {
      return this.segmentedVolumeData.has(seriesKey);
    });

    return new Map(topSeries);
  }

  @cached
  get extremes() {
    let volumeValuesFromAllSeries = Array.from(this.segmentedVolumeData.values())
      .map(getAllValues)
      .flat()
      .compact();
    let metricValuesFromAllSeries = Array.from(this.segmentedMetricData.values())
      .map(getAllValues)
      .flat()
      .compact();
    let volumeKeysFromAllSeries = Array.from(this.segmentedVolumeData.values())
      .map((series) => series.map(([key, _value]) => key as number))
      .flat()
      .compact();

    return {
      minVolumeValue: Math.min(...volumeValuesFromAllSeries),
      maxVolumeValue: Math.max(...volumeValuesFromAllSeries),
      minMetricValue: Math.min(...metricValuesFromAllSeries),
      maxMetricValue: Math.max(...metricValuesFromAllSeries),
      minKey: Math.min(...volumeKeysFromAllSeries),
      maxKey: Math.max(...volumeKeysFromAllSeries),
    };
  }

  buildSeries(seriesId: string, seriesIndex: number): SeriesArearangeOptions {
    let seriesYOffset = seriesIndex;
    let volumeDataSeries = this.segmentedVolumeData.get(seriesId) || [];
    let metricDataSeries = new Map(this.segmentedMetricData.get(seriesId) || []);

    let [topicName, subtopicName] = seriesId.split('=>');
    let palette = this.metricConfig.palette.map((color) => PALETTE[color]);

    return {
      type: 'arearange',
      id: seriesId,
      name: subtopicName ?? topicName,
      zIndex: this.args.limit - seriesIndex + 1,
      colorKey: 'colorAxisValue',
      color: 'var(--neutral-border)', // line colour on top of the area
      data: volumeDataSeries.map(([timestamp, metricValue]: [number, MetricValue]) => {
        let volume = getNumericValue(metricValue);
        if (volume === null) {
          return {
            x: timestamp,
            low: undefined,
            high: undefined,
          };
        }
        let normalizedVolume = maxOverlap * (volume / this.extremes.maxVolumeValue);
        let secondMetricValue = getNumericValue(metricDataSeries.get(timestamp)!);
        return {
          x: timestamp,
          low: seriesYOffset,
          high: seriesYOffset + normalizedVolume,
          volume,
          metricValue: metricDataSeries.get(timestamp),
          colorAxisValue: secondMetricValue,
        } as TimeSeriesPoint;
      }),
      zoneAxis: 'x',
      zones: Array.from(metricDataSeries.entries())
        .map(([timestamp, metricValue]: [number, MetricValue]) => {
          let numericValue = getNumericValue(metricValue);
          if (numericValue === null || timestamp === null) {
            return {
              value: timestamp + this.timeBucketInterval / 2,
              color: colorForNoMetricValue,
            };
          }
          return {
            value: timestamp + this.timeBucketInterval / 2,
            fillColor: getColorForValue(
              palette,
              numericValue,
              this.extremes.minMetricValue,
              this.extremes.maxMetricValue,
            ),
          };
        })
        .compact(),
    };
  }

  buildPlotBandsForWeekends() {
    let plotBands = [] as Highcharts.XAxisPlotBandsOptions[];

    let currentTime = moment
      .tz(this.extremes.minKey, this.args.reportState.dateRange.timezone)
      .startOf('day');
    let endTime = moment.tz(this.extremes.maxKey, this.args.reportState.dateRange.timezone);

    while (currentTime <= endTime) {
      if (currentTime.day() === 6) {
        plotBands.push({
          from: currentTime.valueOf(),
          to: currentTime.add(2, 'days').valueOf(),
          color: 'var(--neutral-container)',
        });
      }
      currentTime.add(1, 'day');
    }
    return plotBands;
  }

  @cached
  get highchartsSeries(): SeriesArearangeOptions[] {
    return Array.from(this.segmentedVolumeData.keys())
      .reverse()
      .map((seriesName: string, seriesIndex) => this.buildSeries(seriesName, seriesIndex));
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::FinAiAgent::Analyze::TopicTrends::TimeSeries': typeof TimeSeries;
  }
}
